import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { User, Mail, Lock, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AccountSectionProps {
  user: { id: string; email?: string } | null;
  userProfile: {
    username: string;
    isSubscriber: boolean;
    isDonator: boolean;
    subscriptionTier: string | null;
  } | null;
}

export const AccountSection = ({ user, userProfile }: AccountSectionProps) => {
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState(userProfile?.username || "");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const handleUsernameChange = async () => {
    if (!user || !newUsername.trim()) return;
    
    setLoading(true);
    try {
      const { error } = await supabase.rpc('change_username', {
        user_id: user.id,
        new_username: newUsername.trim().toLowerCase()
      });

      if (error) throw error;
      
      toast.success('Username updated successfully');
      setIsEditingUsername(false);
    } catch (error: any) {
      console.error('Error updating username:', error);
      toast.error(error.message || 'Failed to update username');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwords.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwords.new
      });

      if (error) throw error;
      
      toast.success('Password updated successfully');
      setIsChangingPassword(false);
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5 text-primary" />
          Account
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            Email
          </Label>
          <div className="flex items-center gap-2">
            <Input
              value={user?.email || ""}
              disabled
              className="bg-muted/50"
            />
            <Check className="h-4 w-4 text-green-500" />
          </div>
          <p className="text-xs text-muted-foreground">Email cannot be changed</p>
        </div>

        {/* Username */}
        <div className="space-y-2">
          <Label>Username</Label>
          {isEditingUsername ? (
            <div className="flex items-center gap-2">
              <Input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter new username"
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={handleUsernameChange}
                disabled={loading || !newUsername.trim()}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsEditingUsername(false);
                  setNewUsername(userProfile?.username || "");
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                value={userProfile?.username || ""}
                disabled
                className="bg-muted/50 flex-1"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditingUsername(true)}
              >
                Edit
              </Button>
            </div>
          )}
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            Password
          </Label>
          {isChangingPassword ? (
            <div className="space-y-3">
              <Input
                type="password"
                placeholder="New password"
                value={passwords.new}
                onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={passwords.confirm}
                onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handlePasswordChange}
                  disabled={loading || !passwords.new || !passwords.confirm}
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswords({ current: "", new: "", confirm: "" });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsChangingPassword(true)}
            >
              Change Password
            </Button>
          )}
        </div>

        {/* Account Type */}
        <div className="space-y-2">
          <Label>Account Type</Label>
          <div className="flex gap-2">
            {userProfile?.isSubscriber && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30">
                {userProfile.subscriptionTier ? `${userProfile.subscriptionTier} Subscriber` : 'Subscriber'}
              </span>
            )}
            {userProfile?.isDonator && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                Donator
              </span>
            )}
            {!userProfile?.isSubscriber && !userProfile?.isDonator && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
                Free Account
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
